import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  X,
  Edit,
  Clock,
  Calendar,
  Image as ImageIcon,
  Plus,
  Send,
  MessageCircle,
  Paperclip,
  FileText,
  File,
  Archive,
  Download,
  Upload,
  AtSign,
  Users,
  Hash,
  Package,
  Star,
  ChevronLeft,
  ChevronRight,
  Settings,
  ExternalLink,
  Share,
  LogIn,
  LogOut,
  BarChart3,
  MapPin,
  AlertTriangle,
  Filter
} from 'lucide-react';
import StageProgress from './StageProgress';
import ProductSettingsModal from './ProductSettingsModal';
import EditProductModal from './EditProductModal';
import SharePointManagementModal from './SharePointManagementModal';
import TimeTrackingModal from './TimeTrackingModal';
import FileUpload from './FileUpload';
import AttachmentsModal from './AttachmentsModal';
import FullscreenImageViewer from './FullscreenImageViewer';
import ImageUploadModal from './ImageUploadModal';
import UserTaggingInput from './UserTaggingInput';
import ProfilePicture from './ProfilePicture';
import LocationModal from './LocationModal';
import KnownIssuesManager from './KnownIssuesManager';
import { useToast } from '../hooks/use-toast';
import { getAbsoluteUrl } from '../utils/urlHelper';
import { formatTimeDuration } from '../utils/timeFormat';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const ExpandedProductView = ({ product, onUpdate, onClose, highlightNoteId }) => {
    const [showEditModal, setShowEditModal] = useState(false);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showFullscreenViewer, setShowFullscreenViewer] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSharePointModal, setShowSharePointModal] = useState(false);
  const [showTimeTrackingModal, setShowTimeTrackingModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(product);
  const [fullscreenViewerIndex, setFullscreenViewerIndex] = useState(0);

  // Keep local product state in sync with props
  useEffect(() => {
    setCurrentProduct(product);
  }, [product]);
  const [currentSession, setCurrentSession] = useState(null);
  const [timeTrackingData, setTimeTrackingData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [filterTimelineOnly, setFilterTimelineOnly] = useState(false); // Will be set from user preferences
  const [documentTypeFilter, setDocumentTypeFilter] = useState('all'); // 'all', 'purchase_order', 'invoice'
  const [isPurchaseOrder, setIsPurchaseOrder] = useState(false);
  const [isInvoice, setIsInvoice] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Check if any filters are active
  const anyFiltersActive = filterTimelineOnly || documentTypeFilter !== 'all';

  // Clear all filters
  const clearAllFilters = () => {
    setFilterTimelineOnly(false);
    setDocumentTypeFilter('all');
    toast({
      title: "Filters Cleared",
      description: "All filters have been reset",
    });
  };

  // Check if user has admin permissions
  console.log('User object for permissions:', user); // Debug log
  const hasAdminPermissions = user?.role === 'admin' || user?.is_admin || user?.can_delete_products;
  console.log('Has admin permissions:', hasAdminPermissions); // Debug log

  const priorityConfig = {
    high: { label: 'High', color: 'bg-red-100 text-red-800 border-red-200' },
    medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    low: { label: 'Low', color: 'bg-green-100 text-green-800 border-green-200' }
  };

  const stageLabels = {
    design_concept: 'Design Concept',
    design_prototype: 'Design Prototype',
    development: 'Development',
    testing_verification: 'Testing & Verification',
    documentation: 'Documentation',
    sales_approval: 'Sales Approval',
    project: 'Project',
    strategy: 'Strategy',
    supply_chain: 'Supply Chain',
    delivery: 'Delivery',
    release: 'Release'
  };

  // Stage colors for timeline notes
  const getStageColor = (stage, noteContent, uploadSource) => {
    // Check if it was added from stage modal (either by uploadSource or content prefix)
    // Content prefix check for backward compatibility with existing notes
    const isFromStage = uploadSource === 'stage' || (noteContent && noteContent.match(/^\[[^\]]+\]/));

    if (isFromStage && stage) {
      const stageColors = {
        design_concept: 'bg-indigo-50 border-l-indigo-400',
        design_prototype: 'bg-purple-50 border-l-purple-400',
        development: 'bg-red-50 border-l-red-400',
        testing_verification: 'bg-orange-50 border-l-orange-400',
        documentation: 'bg-yellow-50 border-l-yellow-400',
        sales_approval: 'bg-orange-50 border-l-orange-400',
        project: 'bg-blue-50 border-l-blue-400',
        strategy: 'bg-purple-50 border-l-purple-400',
        supply_chain: 'bg-orange-50 border-l-orange-400',
        delivery: 'bg-green-50 border-l-green-400',
        release: 'bg-green-50 border-l-green-400'
      };
      return stageColors[stage] || 'bg-gray-50 border-l-gray-400';
    } else {
      // Added from timeline - use black highlight
      return 'bg-gray-50 border-l-gray-900';
    }
  };

  // Stage border colors for file attachments
  const getStageBorderColor = (stage) => {
    const stageBorderColors = {
      design_concept: 'bg-indigo-50 border-indigo-400',
      design_prototype: 'bg-purple-50 border-purple-400',
      development: 'bg-red-50 border-red-400',
      testing_verification: 'bg-orange-50 border-orange-400',
      documentation: 'bg-yellow-50 border-yellow-400',
      sales_approval: 'bg-orange-50 border-orange-400',
      project: 'bg-blue-50 border-blue-400',
      strategy: 'bg-purple-50 border-purple-400',
      supply_chain: 'bg-orange-50 border-orange-400',
      delivery: 'bg-green-50 border-green-400',
      release: 'bg-green-50 border-green-400'
    };
    return stageBorderColors[stage] || 'bg-slate-50 border-slate-400';
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return File;
    const extension = fileName.toLowerCase().split('.').pop();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
      return ImageIcon;
    } else if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(extension)) {
      return FileText;
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return Archive;
    } else {
      return File;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Fetch product notes/timeline
  const fetchNotes = async () => {
    try {
      const response = await api.get(`/product-notes/${product.id}`);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  };

  // Fetch user preferences
  const fetchUserPreferences = async () => {
    try {
      const response = await api.get('/user/preferences');
      const preferences = response.data;
      // Set filter based on user preference, default to false if not set
      setFilterTimelineOnly(preferences.default_timeline_filter || false);
    } catch (error) {
      console.error('Failed to fetch user preferences:', error);
      // Default to false if request fails
      setFilterTimelineOnly(false);
    }
  };

  // Fetch current session for this product
  const fetchCurrentSession = async () => {
    try {
      const response = await api.get(`/products/${product.id}/current-session`);
      setCurrentSession(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch current session:', error);
      }
      setCurrentSession(null);
    }
  };

  // Fetch time tracking data
  const fetchTimeTrackingData = async () => {
    try {
      const response = await api.get(`/products/${product.id}/time-tracking`);
      setTimeTrackingData(response.data);
    } catch (error) {
      console.error('Failed to fetch time tracking data:', error);
    }
  };

  // Handle product login
  const handleProductLogin = async () => {
    try {
      const response = await api.post(`/products/${product.id}/login`, {});

      setCurrentSession(response.data);

      // Trigger global event to update header
      window.dispatchEvent(new CustomEvent('productSessionChange', {
        detail: {
          logged_in: true,
          product_id: product.id,
          product_title: product.title,
          session_id: response.data.id
        }
      }));

      toast({
        title: "Success",
        description: `Logged into ${product.title}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to login to product",
        variant: "destructive",
      });
    }
  };

  // Handle product logout
  const handleProductLogout = async () => {
    try {
      await api.post(`/products/${product.id}/logout`, {});

      setCurrentSession(null);

      // Trigger global event to update header
      window.dispatchEvent(new CustomEvent('productSessionChange', {
        detail: { logged_in: false }
      }));

      toast({
        title: "Success",
        description: `Logged out from ${product.title}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to logout from product",
        variant: "destructive",
      });
    }
  };

  

  useEffect(() => {
    if (product) {
      fetchNotes();
      fetchCurrentSession();
      fetchTimeTrackingData();
      fetchUserPreferences();

      // Auto-refresh notes and session info every 30 seconds for real-time updates
      const notesInterval = setInterval(() => {
        fetchNotes();
        fetchCurrentSession();
      }, 30000);

      // Listen for product session changes from other components
      const handleSessionChange = (event) => {
        const sessionData = event.detail;
        if (!sessionData.logged_in) {
          // Immediate state update for logout
          setCurrentSession(null);
        } else {
          // Fetch fresh session data for login
          fetchCurrentSession();
        }
      };

      window.addEventListener('productSessionChange', handleSessionChange);

      return () => {
        clearInterval(notesInterval);
        window.removeEventListener('productSessionChange', handleSessionChange);
      };
    }
  }, [product]);

  // Scroll to specific note when highlightNoteId changes
  useEffect(() => {
    if (highlightNoteId && notes.length > 0) {
      const timer = setTimeout(() => {
        const noteElement = document.getElementById(`note-${highlightNoteId}`);
        if (noteElement) {
          noteElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          // Add highlight effect
          noteElement.classList.add('ring-2', 'ring-blue-400', 'bg-blue-50');
          setTimeout(() => {
            noteElement.classList.remove('ring-2', 'ring-blue-400', 'bg-blue-50');
          }, 3000);
        }
      }, 500); // Wait for notes to render
      return () => clearTimeout(timer);
    }
  }, [highlightNoteId, notes]);

  

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`${API}/products/${product.id}`);
        onUpdate();
        onClose();
        toast({
          title: "Success",
          description: "Product deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete product",
          variant: "destructive",
        });
      }
    }
  };

  
  const handleUpdate = async (updateData) => {
    try {
      // Check if this is a location update
      const isLocationUpdate = 'location' in updateData;
      const oldLocation = currentProduct.location;
      const newLocation = updateData.location;

      console.log('Location update:', { isLocationUpdate, oldLocation, newLocation, updateData });

      // Update the product
      await api.put(`/products/${product.id}`, updateData);
      
      // Add location change note if location was modified
      if (isLocationUpdate && oldLocation !== newLocation) {
        try {
          let noteContent;
          if (newLocation) {
            if (!oldLocation) {
              // Adding location for the first time
              noteContent = `${user?.full_name || user?.username || 'User'} added the location to: ${newLocation}`;
            } else {
              // Changing existing location
              noteContent = `${user?.full_name || user?.username || 'User'} changed the location to: ${newLocation}`;
            }
          } else {
            // Removing location
            noteContent = `${user?.full_name || user?.username || 'User'} removed the location`;
          }

          await api.post('/product-notes', {
            product_id: product.id,
            user_name: user?.full_name || user?.username || 'User',  // Backend will override with authenticated user
            content: noteContent,
            note_type: 'system_update',
            stage_when_added: product.current_stage
          });
          
          // Refresh notes to show the location change
          fetchNotes();
        } catch (noteError) {
          console.error('Failed to add location change note:', noteError);
        }
      }
      
      // Refresh product data to get updated location
      try {
        const response = await api.get(`/products/${product.id}`);
        const updatedProduct = response.data;

        // Update local product state for immediate UI updates
        setCurrentProduct(updatedProduct);
        
        // Update parent component
        await onUpdate();
        
        // Show success message
        toast({
          title: "Success",
          description: isLocationUpdate ? "Location updated successfully" : "Product updated successfully",
        });
      } catch (refreshError) {
        console.error('Failed to refresh product data:', refreshError);
        // Still call onUpdate as fallback
        onUpdate();
        toast({
          title: "Success",
          description: isLocationUpdate ? "Location updated successfully" : "Product updated successfully",
        });
      }
      
      // Only close edit modal if this was an edit operation
      if (!isLocationUpdate) {
        setShowEditModal(false);
      }
      
      // Close location modal after successful update
      if (isLocationUpdate) {
        setShowLocationModal(false);
      }
    } catch (error) {
      console.error('Update failed:', error);
      toast({
        title: "Error",
        description: "Failed to update product",
        variant: "destructive",
      });
    }
  };

  const handleImagesUploaded = async (uploadedImages) => {
    console.log("Images uploaded:", uploadedImages);
    // Refresh the product data to include new images
    try {
      const response = await api.get(`/products/${product.id}`);
      const updatedProduct = response.data;
      console.log("Updated product data:", updatedProduct);
      
      // Force update the parent component
      await onUpdate();
      
      // Small delay to ensure state update completes
      setTimeout(() => {
        // Force a re-render by checking if images are now available
        console.log("Checking if images are now available:", updatedProduct.images);
      }, 100);
      
      toast({
        title: "Success",
        description: `${uploadedImages.length} image(s) uploaded successfully`,
      });
    } catch (error) {
      console.error('Failed to refresh product data:', error);
      toast({
        title: "Warning", 
        description: "Images uploaded but failed to refresh display. Please close and reopen this view.",
        variant: "destructive",
      });
    }
  };

  const handleSetMainImage = async (imageId) => {
    // Don't allow setting main image for the original product image
    if (imageId === 'original') {
      toast({
        title: "Info",
        description: "This is the main product image and cannot be changed",
      });
      return;
    }
    
    try {
      await axios.put(
        `${API}/products/${product.id}/images/${imageId}/main`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        }
      );
      onUpdate(); // This will refresh the product data in the parent component
      toast({
        title: "Success",
        description: "Image set as main product image",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to set main image",
        variant: "destructive",
      });
    }
  };

  const handleDeleteImage = async (imageId) => {
    // Don't allow deleting the original product image
    if (imageId === 'original') {
      toast({
        title: "Info",
        description: "The main product image cannot be deleted",
      });
      return;
    }
    
    try {
      await axios.delete(
        `${API}/products/${product.id}/images/${imageId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        }
      );
      onUpdate(); // This will refresh the product data in the parent component
      toast({
        title: "Success",
        description: "Image deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive",
      });
    }
  };

  const sendMessagesToTaggedUsers = async (noteContent, noteId) => {
    if (taggedUsers.length === 0) return;

    try {
      const messagePromises = taggedUsers.map(async (taggedUser) => {
        const messageData = {
          recipient_id: taggedUser.id,
          subject: `You were tagged in a note for "${product.title}"`,
          content: `${user?.full_name || user?.username} tagged you in a note:\n\n"${noteContent}"\n\nClick here to view the note and product details.`,
          message_type: 'NOTIFICATION',
          product_id: product.id,
          product_priority: product.priority, // Add product priority for border color
          stage: product.current_stage,
          metadata: {
            note_id: noteId,
            note_content: noteContent,
            tagged_by: user?.id,
            product_title: product.title,
            product_priority: product.priority // Include in metadata as well
          }
        };

        return axios.post(`${API}/messages`, messageData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        });
      });

      await Promise.all(messagePromises);
      console.log(`Sent messages to ${taggedUsers.length} tagged users`);
    } catch (error) {
      console.error('Failed to send messages to tagged users:', error);
      // Don't throw here - we still want the note to be created even if messages fail
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setIsSubmittingNote(true);
    try {
      // Create the note first
      const noteResponse = await api.post('/product-notes', {
        product_id: product.id,
        user_name: user?.full_name || user?.username || 'Unknown User',  // Backend will override with authenticated user
        content: newNote.trim(),
        stage_when_added: product.current_stage,
        tagged_users: taggedUsers.map(u => u.id)
      });

      const createdNote = noteResponse.data;
      
      // Send messages to tagged users
      if (taggedUsers.length > 0) {
        await sendMessagesToTaggedUsers(newNote.trim(), createdNote.id);
      }
      
      setNewNote('');
      setTaggedUsers([]);
      await fetchNotes();
      toast({
        title: "Success",
        description: taggedUsers.length > 0 
          ? `Note added and ${taggedUsers.length} user(s) notified via message`
          : "Note added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add note",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_name', user?.full_name || user?.username || 'Unknown User');
      formData.append('upload_source', 'timeline'); // Mark as uploaded from timeline

      // Add document type information
      if (isPurchaseOrder) {
        formData.append('document_type', 'purchase_order');
      }
      if (isInvoice) {
        formData.append('document_type', 'invoice');
      }

      await api.post(`/product-notes/upload/${product.id}`, formData);

      // Reset checkboxes after upload
      setIsPurchaseOrder(false);
      setIsInvoice(false);

      await fetchNotes();
      setShowFileUpload(false);

      const docType = isPurchaseOrder ? 'Purchase Order' : isInvoice ? 'Invoice' : 'file';
      toast({
        title: "Success",
        description: `${docType} uploaded successfully via timeline`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload file",
        variant: "destructive",
      });
    }
  };

  const handleDownloadFile = (note) => {
    if (note.file_url) {
      const fullUrl = getAbsoluteUrl(note.file_url);
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = note.file_name || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour12: false
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const allImages = [
    ...(product.image_url ? [{
      id: 'original',
      product_id: product.id,
      image_url: product.image_url,
      image_name: product.title,
      is_main: !product.images?.find(img => img.is_main),
      created_at: product.created_at
    }] : []),
    ...(product.images || []),
  ];

  const mainImage = allImages.find(img => img.is_main) || allImages[0];

  if (!product) return null;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 mb-6">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        {/* Product Info */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Badge className={`${priorityConfig[product.priority].color} border dark:bg-opacity-20 dark:border-opacity-30`}>
              {priorityConfig[product.priority].label}
            </Badge>
            <Badge variant="outline" className="text-slate-600 dark:text-slate-300 dark:bg-slate-700 dark:border-slate-600">
              {stageLabels[product.current_stage]}
            </Badge>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2 break-words">
            {product.title}
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            {product.description}
          </p>

          {/* Location Display */}
          {currentProduct.location && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 dark:bg-blue-900/20 dark:border-blue-800">
              <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Location</p>
                <p className="text-sm text-blue-700 dark:text-blue-200">{currentProduct.location}</p>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons - responsive layout */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Product Login/Logout button */}
          {currentSession ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleProductLogout}
              className="flex items-center gap-2 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={handleProductLogin}
              className="flex items-center gap-2 text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
            >
              <LogIn className="w-4 h-4" />
              Login
            </Button>
          )}
          
          {/* Time Tracking button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTimeTrackingModal(true)}
            className="flex items-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Time Tracking
          </Button>
          
          {/* SharePoint button - visible to all users with product access */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSharePointModal(true)}
            className="flex items-center gap-2"
          >
            <Share className="w-4 h-4" />
            SharePoint
          </Button>
          
          {/* Location button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLocationModal(true)}
            className="flex items-center gap-2 dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600"
          >
            <MapPin className="w-4 h-4" />
            {currentProduct?.location ? "Location" : "Add Location"}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Button>
                    
          {/* Settings button - only visible to admins */}
          {hasAdminPermissions && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
            >
              <Settings className="w-4 h-4" />
              Settings
            </Button>
          )}
          {/* Close button - separate on mobile */}
          <div className="w-full sm:w-auto mt-2 sm:mt-0">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="flex items-center gap-2 w-full sm:w-auto"
            >
              <X className="w-4 h-4" />
              Close
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Product Info */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Product Image Gallery */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Images</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowImageUploadModal(true)}
                  className="flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Images</span>
                </Button>
              </div>
              
              {/* Always show main image area */}
              <div className="space-y-4">
                {/* Main image */}
                <div className="relative overflow-hidden rounded-lg bg-slate-100 group cursor-pointer">
                  {mainImage ? (
                    <img
                      src={getAbsoluteUrl(mainImage.image_url)}
                      alt={mainImage.image_name}
                      className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                      onClick={() => {
                        const mainIndex = allImages.findIndex(img => img.id === mainImage.id);
                        setFullscreenViewerIndex(mainIndex);
                        setShowFullscreenViewer(true);
                      }}
                    />
                  ) : (
                    <div className="w-full h-64 flex items-center justify-center">
                      <ImageIcon className="w-16 h-16 text-slate-400" />
                    </div>
                  )}
                  
                  {/* Fallback if no images at all */}
                  {!product.image_url && (!product.images || product.images.length === 0) && (
                    <div className="w-full h-64 flex items-center justify-center">
                      <ImageIcon className="w-16 h-16 text-slate-400" />
                    </div>
                  )}
                  
                                    
                  {/* Image count badge */}
                  {product.images && product.images.length > 0 && (
                    <Badge className="absolute top-2 right-2 bg-black/70 text-white">
                      <ImageIcon className="w-3 h-3 mr-1" />
                      {product.images.length} image{product.images.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                  
                  {/* Main image indicator */}
                  {product.images && product.images.find(img => img.is_main) && (
                    <Badge className="absolute top-2 left-2 bg-yellow-500 text-white">
                      <Star className="w-3 h-3 mr-1" />
                      Main
                    </Badge>
                  )}
                </div>
                
                {/* Thumbnail gallery - show all images */}
                {allImages.length > 1 && (
                  <div className="grid grid-cols-6 gap-1">
                    {allImages.map((image, index) => (
                      <div
                        key={image.id}
                        className="relative overflow-hidden rounded-md bg-slate-100 cursor-pointer group aspect-video"
                        onClick={() => {
                          setFullscreenViewerIndex(index);
                          setShowFullscreenViewer(true);
                        }}
                      >
                        <img
                          src={getAbsoluteUrl(image.image_url)}
                          alt={image.image_name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300" />
                        {/* Main image indicator */}
                        {image.is_main && (
                          <Badge className="absolute top-0.5 left-0.5 bg-yellow-500 text-white text-xs">
                            <Star className="w-2 h-2" />
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Stage Progress */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Progress</h3>
              <StageProgress 
                product={product} 
                onUpdate={onUpdate}
                onTimelineRefresh={fetchNotes}
                defaultExpanded={true}
              />
            </div>

            {/* Product Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Created</span>
                </div>
                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {formatDate(product.created_at)}
                </div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Time Logged</span>
                </div>
                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {formatTimeDuration(product.total_time_logged || 0)}
                </div>
              </div>
            </div>

            
          </div>

          {/* Right Column - Known Issues & Timeline/Notes */}
          <div className="space-y-6">
            {/* Known Issues Section */}
            {product.id && (
              <KnownIssuesManager 
                productId={product.id} 
                currentUser={user?.username} 
              />
            )}

            {/* Timeline & Notes Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Timeline & Notes</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAttachmentsModal(true)}
                  className="flex items-center gap-2"
                >
                  <Paperclip className="w-4 h-4" />
                  Attachments
                </Button>
              </div>

              {/* Timeline Filter */}
              <div className={`flex items-center gap-2 p-3 rounded-lg border ${
                filterTimelineOnly
                  ? 'bg-blue-100 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700'
                  : 'bg-slate-50 border-slate-200 dark:bg-slate-700 dark:border-slate-600'
              }`}>
                <Filter className={`w-4 h-4 ${
                  filterTimelineOnly
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-slate-600 dark:text-slate-400'
                }`} />
                <label className="flex items-center gap-2 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={filterTimelineOnly}
                    onChange={(e) => setFilterTimelineOnly(e.target.checked)}
                    className="rounded border-blue-300 text-blue-600 focus:ring-blue-500 dark:bg-slate-600 dark:border-blue-500"
                  />
                  <span className={`text-sm ${
                    filterTimelineOnly
                      ? 'text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-slate-700 dark:text-slate-300'
                  }`}>
                    Show only timeline notes (hide stage notes)
                  </span>
                </label>
                {filterTimelineOnly && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                    Filtered
                  </Badge>
                )}
              </div>

              {/* Document Type Filter */}
              <div className={`flex items-center gap-2 p-3 rounded-lg border ${
                documentTypeFilter !== 'all'
                  ? 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-700'
                  : 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
              }`}>
                <Filter className={`w-4 h-4 ${
                  documentTypeFilter !== 'all'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-green-600 dark:text-green-400'
                }`} />
                <div className="flex items-center gap-2 flex-1">
                  <span className={`text-sm ${
                    documentTypeFilter !== 'all'
                      ? 'text-green-700 dark:text-green-300 font-medium'
                      : 'text-green-700 dark:text-green-300'
                  }`}>Show:</span>
                  <select
                    value={documentTypeFilter}
                    onChange={(e) => setDocumentTypeFilter(e.target.value)}
                    className="text-sm border border-green-300 rounded px-2 py-1 bg-white dark:bg-green-800 dark:border-green-600 text-green-800 dark:text-green-200"
                  >
                    <option value="all">All Documents</option>
                    <option value="purchase_order">Purchase Orders Only</option>
                    <option value="invoice">Invoices Only</option>
                  </select>
                </div>
                {documentTypeFilter !== 'all' && (
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                    {documentTypeFilter === 'purchase_order' ? 'Purchase Orders' : 'Invoices'}
                  </Badge>
                )}
              </div>

              {/* Clear All Filters Button */}
              {anyFiltersActive && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearAllFilters}
                    className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                  >
                    <Filter className="w-4 h-4" />
                    Clear All Filters
                  </Button>
                </div>
              )}

              {/* Add Note Form */}
              <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 space-y-3">
                <UserTaggingInput
                  content={newNote}
                  onContentChange={setNewNote}
                  taggedUsers={taggedUsers}
                  onTaggedUsersChange={setTaggedUsers}
                  placeholder="Add a note to the timeline... Use @username to tag users"
                  productId={product.id}
                />
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || isSubmittingNote}
                    size="sm"
                    className="flex-1"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isSubmittingNote ? 'Adding...' : 'Add Note'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFileUpload(!showFileUpload)}
                    className="flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    File
                  </Button>
                </div>
                
                {/* File Upload Section */}
                {showFileUpload && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <FileUpload onFileUpload={handleFileUpload} multiple={true} />

                    {/* Document Type Checkboxes - Only for timeline uploads */}
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-sm font-medium text-blue-900 mb-2">Document Type (Applied to all files)</div>
                      <div className="text-xs text-blue-600 mb-2">This will be applied to all selected files</div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isPurchaseOrder}
                            onChange={(e) => setIsPurchaseOrder(e.target.checked)}
                            className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-blue-700">Purchase Order</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isInvoice}
                            onChange={(e) => setIsInvoice(e.target.checked)}
                            className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-blue-700">Invoice</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {(() => {
                  // Filter notes based on both timeline and document type filters
                  let filteredNotes = filterTimelineOnly
                    ? notes.filter(note => !note.content.match(/^\[[^\]]+\]/)) // Hide stage notes
                    : notes;

                  // Apply document type filter (only to file attachments)
                  if (documentTypeFilter !== 'all') {
                    filteredNotes = filteredNotes.filter(note => {
                      if (note.note_type !== 'file_attachment') return false;
                      return note.document_type === documentTypeFilter;
                    });
                  }

                  if (filteredNotes.length === 0) {
                    let emptyMessage = "No notes yet. Add the first note to start the timeline.";

                    if (filterTimelineOnly && documentTypeFilter !== 'all') {
                      emptyMessage = `No ${documentTypeFilter === 'purchase_order' ? 'Purchase Orders' : 'Invoices'} found in timeline notes.`;
                    } else if (filterTimelineOnly) {
                      emptyMessage = "No timeline notes found. Stage notes are hidden by the current filter.";
                    } else if (documentTypeFilter !== 'all') {
                      emptyMessage = `No ${documentTypeFilter === 'purchase_order' ? 'Purchase Orders' : 'Invoices'} found.`;
                    }

                    return (
                      <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                        <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>{emptyMessage}</p>
                      </div>
                    );
                  }

                  return filteredNotes.map((note) => {
                    const stageColorClass = getStageColor(note.stage_when_added, note.content, note.upload_source);
                    const isSystemNote = note.note_type === 'system_update';
                    const isFileAttachment = note.note_type === 'file_attachment';

                    return (
                      <div
                        key={note.id}
                        id={`note-${note.id}`}
                        className={`border border-slate-200 rounded-lg p-3 border-l-4 ${stageColorClass} ${
                          isSystemNote ? 'bg-blue-25' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {note.user_name}
                            </div>
                            {isSystemNote && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                System
                              </Badge>
                            )}
                            {isFileAttachment && (
                              <>
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                  File
                                </Badge>
                                {note.document_type === 'purchase_order' && (
                                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                    Purchase Order
                                  </Badge>
                                )}
                                {note.document_type === 'invoice' && (
                                  <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700">
                                    Invoice
                                  </Badge>
                                )}
                              </>
                            )}
                            {note.tagged_users && note.tagged_users.length > 0 && (
                              <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 flex items-center gap-1">
                                <AtSign className="w-3 h-3" />
                                {note.tagged_users.length} tagged
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {formatDateTime(note.created_at)}
                          </div>
                        </div>

                        {/* File attachment display */}
                        {isFileAttachment && note.file_url && (
                          <div className="mb-2">
                            <div className={`flex items-center gap-2 p-2 rounded border ${
                              (note.upload_source === 'stage' || (note.content && note.content.match(/^\[[^\]]+\]/))) && note.stage_when_added
                                ? getStageBorderColor(note.stage_when_added)
                                : 'bg-slate-50 border-slate-200'
                            }`}>
                              {React.createElement(getFileIcon(note.file_name), {
                                className: "w-4 h-4 text-slate-600"
                              })}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                                  {note.file_name}
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                  {(note.upload_source === 'stage' || (note.content && note.content.match(/^\[[^\]]+\]/))) && note.stage_when_added
                                    ? `Added via ${stageLabels[note.stage_when_added]}`
                                    : 'Added via timeline'
                                  }
                                </div>
                              </div>
                              {note.file_size && (
                                <span className="text-xs text-slate-500 dark:text-slate-400 flex-shrink-0">
                                  {formatFileSize(note.file_size)}
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadFile(note)}
                                className="h-6 w-6 p-0 flex-shrink-0"
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}

                        <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                          {note.content}
                        </div>

                        {note.stage_when_added && (
                          <div className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                            Added during: {stageLabels[note.stage_when_added]}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      
      <EditProductModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdate}
        product={product}
      />

      <AttachmentsModal
        isOpen={showAttachmentsModal}
        onClose={() => setShowAttachmentsModal(false)}
        productId={product.id}
        productTitle={product.title}
      />

      <ImageUploadModal
        product={product}
        isOpen={showImageUploadModal}
        onClose={() => setShowImageUploadModal(false)}
        onImagesUploaded={handleImagesUploaded}
      />

      <ProductSettingsModal
        product={product}
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onUpdate={onUpdate}
        onDelete={handleDelete}
      />

      <SharePointManagementModal
        product={product}
        isOpen={showSharePointModal}
        onClose={() => setShowSharePointModal(false)}
        onUpdate={onUpdate}
      />

      <TimeTrackingModal
        product={product}
        isOpen={showTimeTrackingModal}
        onClose={() => setShowTimeTrackingModal(false)}
      />

      <LocationModal
        product={currentProduct}
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onSubmit={handleUpdate}
      />

      <FullscreenImageViewer
        images={allImages}
        currentIndex={fullscreenViewerIndex}
        isOpen={showFullscreenViewer}
        onClose={() => setShowFullscreenViewer(false)}
        onSetMainImage={handleSetMainImage}
        onDeleteImage={handleDeleteImage}
        canManage={product.images && product.images.length > 0} // Only allow management for uploaded images (not original)
      />
    </div>
  );
};

export default ExpandedProductView;